import { Link } from "react-router-dom";
import { Mail, Phone, Instagram } from "lucide-react";
import { LogoMark } from "@/components/Navbar";
import { LEGAL_CONTACT } from "@/components/LegalLayout";
import api from "@/lib/api";
import { toast } from "sonner";

const Marquee = () => (
  <div className="overflow-hidden border-y border-leaf/20 bg-forest py-4 text-cream" data-testid="footer-claim-band">
    <div className="flex w-max animate-marquee gap-0">
      {[0, 1].map((half) => (
        <div key={half} aria-hidden={half === 1} className="flex shrink-0 items-center font-mono text-xs uppercase tracking-[0.3em]">
          {["100% Natural", "No Preservatives", "12 Single-Ingredient Powders", "Eat Real. Feel Good.", "Goodness, Naturally", "No Added Colours"].map((t) => (
            <span key={t} className="flex items-center">
              <span className="px-6">{t}</span>
              <span className="text-leaf">✦</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const legalLinks = [
  { to: "/terms", label: "Terms of Use", testid: "footer-terms-link" },
  { to: "/privacy", label: "Privacy Policy", testid: "footer-privacy-link" },
  { to: "/refund-policy", label: "Returns & Refunds", testid: "footer-refund-link" },
  { to: "/shipping-policy", label: "Shipping Policy", testid: "footer-shipping-link" },
];

export default function Footer() {
  const subscribe = async (e) => {
    e.preventDefault();
    const email = e.currentTarget.email.value;
    try {
      await api.post("/enquiries", { name: "Newsletter", email, btype: "newsletter", message: "Newsletter signup" });
      toast.success("You're on the list. Goodness incoming.");
      e.currentTarget.reset();
    } catch {
      toast.error("Couldn't sign you up. Try again?");
    }
  };

  return (
    <footer className="relative bg-forest text-cream">
      <Marquee />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-[1.4fr,1fr,1fr]">
        <div>
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark />
            <span className="font-display text-2xl font-extrabold lowercase tracking-tight">abhivesha</span>
          </Link>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream/70">
            12 single-ingredient kitchen powders. 100% natural, nothing added. Made for people who love good food and hate the prep.
          </p>
          <p className="mt-6 font-hand text-2xl text-leaf">goodness, naturally.</p>
        </div>

        <div>
          <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Explore</h3>
          <ul className="mt-5 space-y-3 text-sm font-semibold">
            <li><Link to="/shop" className="text-cream/80 transition-colors hover:text-leaf">Shop all powders</Link></li>
            <li><Link to="/distribute" className="text-cream/80 transition-colors hover:text-leaf">Distribute with us</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="text-cream/80 transition-colors hover:text-leaf">Contact us</Link></li>
          </ul>
          <h3 className="mt-8 font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Legal</h3>
          <ul className="mt-5 space-y-3 text-sm font-semibold">
            {legalLinks.map((l) => (
              <li key={l.to}><Link to={l.to} data-testid={l.testid} className="text-cream/80 transition-colors hover:text-leaf">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Talk to us</h3>
          <ul className="mt-5 space-y-3 text-sm font-semibold text-cream/80">
            <li>
              <a data-testid="footer-email-link" href={`mailto:${LEGAL_CONTACT.email}`} className="flex items-center gap-2 transition-colors hover:text-leaf">
                <Mail className="h-4 w-4 text-leaf" /> {LEGAL_CONTACT.email}
              </a>
            </li>
            <li>
              <a data-testid="footer-phone-link" href="tel:+917020724570" className="flex items-center gap-2 transition-colors hover:text-leaf">
                <Phone className="h-4 w-4 text-leaf" /> {LEGAL_CONTACT.phone}
              </a>
            </li>
            <li>
              <a data-testid="footer-instagram-link" href="https://instagram.com/abhiveshafoods" target="_blank" rel="noreferrer" className="flex items-center gap-2 transition-colors hover:text-leaf">
                <Instagram className="h-4 w-4 text-leaf" /> @abhiveshafoods
              </a>
            </li>
          </ul>
          <form onSubmit={subscribe} className="mt-6 flex gap-2">
            <input
              data-testid="footer-newsletter-input"
              name="email"
              type="email"
              required
              placeholder="your@email.com"
              className="w-full rounded-full border border-cream/20 bg-cream/10 px-4 py-2.5 text-sm text-cream placeholder:text-cream/40 focus:border-leaf"
            />
            <button data-testid="footer-newsletter-submit" className="shrink-0 rounded-full bg-leaf px-5 text-sm font-bold text-white transition-colors hover:bg-cream hover:text-forest">
              Join
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6">
        <p className="rounded-2xl border border-cream/10 bg-cream/5 px-5 py-4 text-center font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-cream/50" data-testid="footer-compliance">
          Abhivesha Foods — a proprietary firm of {LEGAL_CONTACT.entity} · FSSAI Lic. No. {LEGAL_CONTACT.fssai} · GSTIN {LEGAL_CONTACT.gst} · All payments collected by {LEGAL_CONTACT.entity}
        </p>
      </div>
      <div className="overflow-hidden border-t border-cream/10 py-6 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-cream/40">
        © 2025 {LEGAL_CONTACT.entity} · Eat Real. Feel Good. · <Link to="/terms" className="hover:text-leaf">Terms</Link> · <Link to="/privacy" className="hover:text-leaf">Privacy</Link>
      </div>
    </footer>
  );
}
